import classNames from 'classnames';
import FloorplansButton from 'components/floorplansButton';
import { FavouriteButton } from 'components/shared/favouriteButton';
import FeatureIconBase from 'components/shared/featureIcon';
import useFavourite from 'hooks/useFavourite';
import { Room, Venue } from 'hooks/useVenue';
import getConfig from 'lib/next-config-compat';
import Link from 'next/link';
import { FC, HTMLProps, useContext, useMemo } from 'react';

import { Marker, StaticGoogleMap } from 'react-static-google-map';
import { AuthContext } from 'state/auth';
import { filteredKeyFacets } from 'utils/filters';
import { LatLng } from 'utils/geolocation';
import { useTranslation } from 'utils/i18n';
import { getVenueImage } from 'utils/venueImages';

const { publicRuntimeConfig: { googleMapsApiKey } = {} } = getConfig();

interface FeatureIconProps {
  type?: string;
  name?: string;
  additionalText?: string;
  icon?: string;
  iconAltText?: string;
  displaySvgIcon?: boolean;
}

const FeatureIcon = FeatureIconBase as unknown as FC<FeatureIconProps>;

interface Props extends HTMLProps<HTMLDivElement> {
  venue: Venue;
  location: string;
  showRooms?: boolean;
  showBookRoomsButton?: boolean;
  showFeatures?: boolean;
  floorplansButtonPosition?: 'none' | 'image' | 'feature';
  imagePosition?: 'left' | 'top';
  showMapOnThumbNail?: boolean;
  onClickMap?: (latLng: LatLng) => void;
  organisationName?: string;
}

const MapVenue: FC<Props> = ({
  venue,
  location,
  showRooms = false,
  showBookRoomsButton = false,
  showFeatures = false,
  floorplansButtonPosition = 'feature',
  imagePosition = 'left',
  className: classNameProp,
  showMapOnThumbNail = false,
  onClickMap,
  organisationName,
  ...props
}) => {
  const { t } = useTranslation('common');
  const className = classNames(
    'search-map__venue',
    {
      'search-map__venue__image-top': imagePosition === 'top',
    },
    classNameProp
  );

  const { loggedIn } = useContext(AuthContext);
  const [favourited, toggleFavourite] = useFavourite('venues', venue.id);

  const { clinicalRooms = [], nonClinicalRooms = [], facets = [] } = venue;

  const clinicalTotal = useMemo(
    () => clinicalRooms.reduce((prev, { count }) => prev + count, 0),
    [clinicalRooms]
  );
  const nonClinicalTotal = useMemo(
    () => nonClinicalRooms.reduce((prev, { count }) => prev + count, 0),
    [nonClinicalRooms]
  );

  const venueUrl = `${t('navigation.venues.url')}/${venue.id}`;

  const renderRoomList = ({ name, count }: Room) => (
    <div key={name} className="search-map__space-rooms">
      <span className="search-map__space-quantity">{count}</span>
      <p>{name}</p>
    </div>
  );

  const venueImage = getVenueImage(
    venue,
    imagePosition === 'top' ? 'regular' : 'thumbnail'
  );
  const imageStyle = venueImage
    ? { backgroundImage: `url(${venueImage})` }
    : {};

  return (
    <div className={className} {...props}>
      <div
        className="search-map__venue-image"
        data-testid="venueImage"
        style={imageStyle}
      >
        {!venueImage && (
          <span className="search-map__no-image">
            {t('shared.picture_coming_soon')}
          </span>
        )}
        {venueImage && showMapOnThumbNail && (
          <button
            type="button"
            className="venue__map"
            data-testid="venueMap"
            title={t('venue_list.view_venue_on_map')}
            onClick={() => onClickMap?.(venue.latLng!)}
          >
            {!!venue.latLng && (
              <StaticGoogleMap
                size="153x138"
                zoom={14}
                apiKey={googleMapsApiKey}
              >
                <Marker location={venue.latLng} color="blue" />
              </StaticGoogleMap>
            )}
            {!venue.latLng && (
              <div>
                Map
                <br />
                coming soon
              </div>
            )}
          </button>
        )}
        {floorplansButtonPosition === 'image' && (
          <FloorplansButton venue={venue} />
        )}
      </div>
      <div className="search-map__venue-content">
        <div className="search-map__venue-line">
          <h2 className="search-map__venue-name">
            <Link href={venueUrl}>{venue.name}</Link>
          </h2>

          {loggedIn && (
            <FavouriteButton
              favouritedState={favourited}
              type="venues"
              handleFavouriteButtonClick={toggleFavourite}
            />
          )}
        </div>

        <h4 className="search-map__venue-organisation">
          <span>{t('shared.managed_by')}:&nbsp;</span>
          {`${organisationName}`}
        </h4>

        {!!venue.distance && !!location && (
          <div className="search-map__venue-distance-container">
            <p
              className="search-map__venue-distance"
              data-testid="distanceLabel"
            >
              <i className="fas fa-map-marker-alt" />
              {t('search_results.miles_from_location', {
                count: venue.distance,
                location,
              })}
            </p>
          </div>
        )}

        <ul className="search-map__venue-space-types">
          {(!showRooms || clinicalTotal > 0) && (
            <li className="search-map__venue-space-type search-map__venue-space-type--clinical">
              <div className="search-map__space-room-types">
                {!showRooms && (
                  <span className="search-map__space-quantity">
                    {clinicalTotal}
                  </span>
                )}
                <p>clinical rooms</p>
              </div>
              {showRooms && clinicalRooms.map(renderRoomList)}
            </li>
          )}
          {(!showRooms || nonClinicalTotal > 0) && (
            <li className="search-map__venue-space-type search-map__venue-space-type--non-clinical">
              <div className="search-map__space-room-types">
                {!showRooms && (
                  <span className="search-map__space-quantity">
                    {nonClinicalTotal}
                  </span>
                )}
                <p>non-clinical rooms</p>
              </div>
              {showRooms && nonClinicalRooms.map(renderRoomList)}
            </li>
          )}
        </ul>

        <div className="search-map__venue-footer">
          {showBookRoomsButton && (
            <Link
              href={`${venueUrl}#venue-rooms-section`}
              className="button button--primary"
            >
              {t('venues.book_rooms')}
            </Link>
          )}

          {showFeatures && (
            <ul className="venue__feature-list">
              {floorplansButtonPosition === 'feature' && (
                <FloorplansButton venue={venue} wrapper="li" />
              )}
              {filteredKeyFacets(facets).map(facet => (
                <li key={facet.name}>
                  <FeatureIcon {...facet} displaySvgIcon={false} />
                </li>
              ))}
            </ul>
          )}
          {!showFeatures && floorplansButtonPosition === 'feature' && (
            <FloorplansButton venue={venue} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MapVenue;
